
import { convertBaseModelToHTML, convertHTMLToBaseModel, createEmptyDoc } from "./doc";
import { EditorSelection } from "./Selection";

export class Editor {
  constructor(root) {
    this.root = root;
    this.selection = new EditorSelection(this);
    this.state = {
      doc: createEmptyDoc(),
      selection: null,
    }
    this.selectionState = { anchor: 0, focus: 0 };
    this.version = 0;
    this.commands = undefined;
    this.history = undefined;
    this.normalize = undefined;
  }

  renderBaseModel({ doc }) {
    return convertBaseModelToHTML({ doc });
  }

  createSnapshot() {
    const serializedSelection = this.selection.save();

    return {
      doc: convertHTMLToBaseModel({ html: this.root.innerHTML }),
      selection: serializedSelection
        ? {
          anchor: JSON.stringify(serializedSelection.start),
          focus: JSON.stringify(serializedSelection.end),
        }
        : null,
    };
  }

  createSnapshotFromHTML({ html }) {
    const container = document.createElement("div");
    container.innerHTML = html || "<p><br></p>";

    const doc = convertHTMLToBaseModel({
      html: container.innerHTML,
    });

    const selection = this.createSelectionAtEnd(doc);

    const snapshot = {
      doc,
      selection,
    };

    return snapshot;
  }

  createSelectionAtEnd(doc) {
    if (!Array.isArray(doc) || doc.length === 0) {
      return {
        anchor: { path: [0, 0], offset: 0 },
        focus: { path: [0, 0], offset: 0 },
      };
    }

    return {
      anchor: this.resolveAnchorFromLength({ doc, lngth: doc.length }),
      focus: this.resolveAnchorFromLength({ doc, lngth: doc.length })
    }
  }

  getEndPosition(doc) {
    let lastPath = [0, 0];
    let offset = 0;

    function walk(node, path) {
      if (!node) return;

      if (node.type === "text") {
        lastPath = path;
        offset = (node.content || "").length;
      }

      if (node.children) {
        node.children.forEach((child, i) => {
          walk(child, [...path, i]);
        });
      }
    }

    doc.forEach((block, i) => {
      walk(block, [i]);
    });

    return {
      path: lastPath,
      offset,
    };
  }

  resolveAnchorFromLength = ({ doc, length }) => {
    let globalOffset = 0;

    function walk(node, path) {
      if (!node) return null;

      if (node.type === "text") {
        const text = node.content || "";
        const textLength = text.length;

        const start = globalOffset;
        const end = globalOffset + textLength;

        if (length >= start && length <= end) {
          return {
            path,
            offset: length - start,
          };
        }

        globalOffset = end;
        return null;
      }

      if (Array.isArray(node.children)) {
        for (let i = 0; i < node.children.length; i++) {
          const result = walk(node.children[i], [...path, i]);
          if (result) return result;
        }
      }

      return null;
    }

    for (let blockIndex = 0; blockIndex < doc.length; blockIndex++) {
      const result = walk(doc[blockIndex], [blockIndex]);
      if (result) return result;
    }

    return this.getEndPosition(doc);
  }

  updateSnapshot() {
    const serializedSelection = this.selection.save();

    return {
      doc: convertHTMLToBaseModel({ html: this.root.innerHTML }),
      selection: serializedSelection
        ? {
          anchor: JSON.stringify(serializedSelection.start),
          focus: JSON.stringify(serializedSelection.end),
        }
        : null,
    };
  }

  restoreSnapshot(snapshot) {
    const { doc, selection } = snapshot;

    if (!doc) {
      return snapshot;
    }

    const innerHTML = convertBaseModelToHTML({ doc });
    this.root.innerHTML = innerHTML;

    this.restoreSelection(selection)

    // Dispatch input event
    const event = new Event("input", { bubbles: true, cancelable: true });
    this.root.dispatchEvent(event);
    return snapshot;
  }

  getState() {
    return {
      html: this.root.innerHTML,
    };
  }

  update(fn) {
    fn();
    // Convert DOM -> BaseModel
    const baseModel = convertHTMLToBaseModel({
      html: this.root.innerHTML,
    });

    this.state.doc = baseModel;

    this.normalize?.();

    const event = new Event("input", { bubbles: true, cancelable: true });
    this.root.dispatchEvent(event);
  }

  restoreSelection(selection) {
    requestAnimationFrame(() => {
      // Restore selection if exists
      if (selection) {
        try {
          const start = JSON.parse(selection.anchor);
          const end = JSON.parse(selection.focus);
          this.selection.restore({ start, end });
        } catch (error) {
          this.selection.setAtEnd();
        }
      } else {
        this.selection.setAtEnd();
      }
    });
  }
}
